// =============================================================================
// src/features/crisis/crisis-view-switch.ts
// Alternador de estados de demonstracao (crise / IA analisando / calmo).
//
// Proveniencia: porte FIEL de assets/crisis.js 291-325 (setView + segBtns).
//   Alterna a visibilidade das views, a cor do ponto de contexto, o estado
//   ativo dos segmentos, reconstroi o ticker para o novo estado e rola o painel
//   ao topo (smooth, ou auto com prefers-reduced-motion).
//
// SRP: so a troca de estado de demonstracao. O ticker e injetado (DI) para
//   reconstruir o feed; o controller nunca instancia o ticker.
// =============================================================================

import { on, qs, qsa } from '../../app/dom';
import type { CrisisTickerController } from './crisis-ticker';
import type { CrisisTickerView } from './crisis-ticker-feed';

/** Estados de demonstracao expostos pelos botoes de segmento (data-crz-view). */
type CrisisDemoView = 'crisis' | 'analyzing' | 'calm';

/** Cor do ponto de contexto por estado (assets/crisis.js 311-312). */
const CONTEXT_DOT_COLOR = {
  calm: { background: '#1D9E75', halo: '#EAF3DE' },
  alert: { background: '#BA7517', halo: '#FAEEDA' },
} as const;

/** Referencias das views/segmentos resolvidas no init (assets/crisis.js 292-298). */
interface CrisisViewRefs {
  readonly viewCrisis: HTMLElement;
  readonly viewCalm: HTMLElement;
  readonly aiReady: HTMLElement;
  readonly aiAnalyzing: HTMLElement;
  readonly ctxDot: HTMLElement | null;
  readonly scrollEl: HTMLElement | null;
  readonly segButtons: readonly HTMLElement[];
}

/**
 * Controla a troca de estado de demonstracao. Comeca em 'crisis' (assets/
 * crisis.js 299); setView reconstroi o ticker via o controller injetado.
 */
export class CrisisViewSwitch {
  private currentView: CrisisDemoView = 'crisis';

  constructor(
    private readonly takeover: HTMLElement,
    private readonly ticker: CrisisTickerController,
    private readonly reduceMotion: boolean,
  ) {}

  /** Liga os botoes de segmento; no-op se faltarem views essenciais. */
  bind(): void {
    const refs = this.collectRefs();
    if (!refs) return;
    refs.segButtons.forEach((btn) => this.bindSegment(refs, btn));
  }

  /** Estado de demonstracao atual (consumido pelo controller para o draw inicial). */
  get view(): CrisisDemoView {
    return this.currentView;
  }

  /** Resolve views e segmentos; retorna null se algum essencial faltar. */
  private collectRefs(): CrisisViewRefs | null {
    const viewCrisis = qs<HTMLElement>('#crzViewCrisis');
    const viewCalm = qs<HTMLElement>('#crzViewCalm');
    const aiReady = qs<HTMLElement>('#crzAiReady');
    const aiAnalyzing = qs<HTMLElement>('#crzAiAnalyzing');
    if (!viewCrisis || !viewCalm || !aiReady || !aiAnalyzing) return null;
    return {
      viewCrisis,
      viewCalm,
      aiReady,
      aiAnalyzing,
      ctxDot: qs<HTMLElement>('#crzCtxDot'),
      scrollEl: qs<HTMLElement>('.crz-scroll', this.takeover),
      segButtons: qsa<HTMLElement>('.crz-seg-btn', this.takeover),
    };
  }

  /** Sincroniza aria-pressed e liga o clique de um segmento (assets/crisis.js 322-325). */
  private bindSegment(refs: CrisisViewRefs, btn: HTMLElement): void {
    btn.setAttribute('aria-pressed', btn.classList.contains('active') ? 'true' : 'false');
    on(btn, 'click', () => this.setView(refs, readDemoView(btn)));
  }

  /** Aplica o estado `v`: visibilidade, ponto, segmentos, ticker e scroll (assets/crisis.js 301-321). */
  private setView(refs: CrisisViewRefs, v: CrisisDemoView): void {
    this.currentView = v;
    this.toggleViews(refs, v);
    this.paintContextDot(refs, v === 'calm');
    this.syncSegments(refs, v);
    this.ticker.renderTicker(v === 'calm' ? 'calm' : 'crisis');
    this.scrollToTop(refs);
  }

  /** Mostra/oculta crise/calmo e os sub-estados pronto/analisando (assets/crisis.js 303-308). */
  private toggleViews(refs: CrisisViewRefs, v: CrisisDemoView): void {
    refs.viewCrisis.hidden = v === 'calm';
    refs.viewCalm.hidden = v !== 'calm';
    if (v === 'calm') return;
    refs.aiReady.hidden = v === 'analyzing';
    refs.aiAnalyzing.hidden = v !== 'analyzing';
  }

  /** Pinta o ponto de contexto (verde calmo / ambar atencao) (assets/crisis.js 309-313). */
  private paintContextDot(refs: CrisisViewRefs, calm: boolean): void {
    if (!refs.ctxDot) return;
    const palette = calm ? CONTEXT_DOT_COLOR.calm : CONTEXT_DOT_COLOR.alert;
    refs.ctxDot.style.background = palette.background;
    refs.ctxDot.style.boxShadow = `0 0 0 3px ${palette.halo}`;
  }

  /** Marca o segmento ativo + aria-pressed (assets/crisis.js 314-318). */
  private syncSegments(refs: CrisisViewRefs, v: CrisisDemoView): void {
    refs.segButtons.forEach((btn) => {
      const on = readDemoView(btn) === v;
      btn.classList.toggle('active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  /** Rola o painel ao topo (smooth, ou auto se reduce-motion) (assets/crisis.js 320). */
  private scrollToTop(refs: CrisisViewRefs): void {
    refs.scrollEl?.scrollTo({ top: 0, behavior: this.reduceMotion ? 'auto' : 'smooth' });
  }
}

/** Le o data-crz-view de um botao, com fallback para 'crisis'. */
function readDemoView(btn: HTMLElement): CrisisDemoView {
  const raw = btn.dataset.crzView;
  if (raw === 'analyzing' || raw === 'calm') return raw;
  return 'crisis';
}

/** Garante o estreitamento de tipo do estado calmo para o ticker (apenas crisis|calm). */
export function asTickerView(v: CrisisDemoView): CrisisTickerView {
  return v === 'calm' ? 'calm' : 'crisis';
}
